import { useEffect } from "react";
import { Brain, CreditCard, Inbox, Settings as SettingsIcon, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import AIInstructionsPanel from "@/components/brand/AIInstructionsPanel";
import AppearancePanel from "@/components/brand/settings/AppearancePanel";
import TeamPanel from "@/components/brand/settings/TeamPanel";
import BillingPanel from "@/components/brand/settings/BillingPanel";
import RequestsPanel from "@/components/brand/settings/RequestsPanel";

const BASE_TABS = [
  { key: "appearance", icon: SettingsIcon, labelKey: "settings.tab_appearance" },
  { key: "team", icon: Users, labelKey: "settings.tab_team" },
  { key: "ai", icon: Brain, labelKey: "settings.tab_ai" },
  { key: "billing", icon: CreditCard, labelKey: "settings.tab_billing" },
];

const OWNER_TABS = [
  { key: "requests", icon: Inbox, labelKey: "settings.tab_requests" },
];

export default function SettingsHub({ brand, role, tab, setTab, onUpdated }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const isOwner = !!user && brand.owner_user_id === user.user_id;
  const TABS = isOwner ? [...BASE_TABS, ...OWNER_TABS] : BASE_TABS;

  // Fall back to appearance when the active tab no longer exists.
  useEffect(() => {
    if (!TABS.find((it) => it.key === tab)) setTab("appearance");
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-full bg-white">
      <div className="max-w-6xl mx-auto px-10 pt-14 pb-16">
        <h1 className="font-display text-5xl font-medium tracking-tight text-[#1a1a1a] mb-10" data-testid="settings-title">
          {t("settings.title")}
        </h1>

        <div className="grid grid-cols-[200px_1fr] gap-10 items-start">
          {/* Inner sidebar */}
          <nav
            data-testid="settings-tabs"
            className="rounded-xl bg-[#f3f3f1] p-2 flex flex-col gap-1 sticky top-6"
          >
            {TABS.map((it) => {
              const Icon = it.icon;
              const active = tab === it.key;
              return (
                <button
                  key={it.key}
                  data-testid={`settings-tab-${it.key}`}
                  onClick={() => setTab(it.key)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-[14px] text-left transition-colors ${
                    active ? "bg-white text-[#1a1a1a] font-medium shadow-sm" : "text-[#1a1a1a]/80 hover:bg-white/60"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.75} /> {t(it.labelKey)}
                </button>
              );
            })}
          </nav>

          {/* Panels */}
          <div className="min-w-0 space-y-10">
            {tab === "appearance" && <AppearancePanel brand={brand} onUpdated={onUpdated} />}
            {tab === "team" && <TeamPanel brand={brand} />}
            {tab === "ai" && <AIInstructionsPanel brand={brand} role={role} onUpdated={onUpdated} />}
            {tab === "billing" && <BillingPanel brand={brand} role={role} />}
            {tab === "requests" && isOwner && <RequestsPanel brand={brand} />}
          </div>
        </div>
      </div>
    </div>
  );
}
